import { Navigate, createBrowserRouter } from 'react-router';

import { PageWrapper } from 'app/PageWrapper';
import {
  ConfirmPassword,
  Help,
  Login,
  PassportCaptureWizard,
  Profile,
  ProfileMain,
  Questions,
  Settings,
  Tasks,
  Teacher,
  TeacherEdit,
  TeacherHelp,
  TeacherNotificationsPage,
  TeacherProfile,
  TeacherSearch,
  TeacherSearchError,
  TeacherSearchErrorPay,
  TeacherSearchErrorWait,
  TeacherSearchFiltersItem,
  TeacherSettings,
  TeacherTrustScorePage,
  StudentReviewPage,
  NotificationsPage,
  VerificationEdits,
} from 'modules';
import { Chat as ChatV2 } from 'modules/ChatV2/Chat';
import { PasswordConfirm } from 'modules/PasswordConfirm/index';
import { ForgotPassword } from 'modules/PasswordRecovery/ForgotPassword';
import { ResetPassword } from 'modules/PasswordRecovery/ResetPassword';
import { ResetSuccess } from 'modules/PasswordRecovery/ResetSuccess';
import { OtpChallengePage } from 'modules/Otp/OtpChallengePage';
import { PersonalDataConfirmPage } from 'modules/PersonalData/PersonalDataConfirmPage';
import { PersonalDataOtpPage } from 'modules/PersonalData/PersonalDataOtpPage';
import { PersonalDataRejectPage } from 'modules/PersonalData/PersonalDataRejectPage';
import { ConfirmEmail } from 'modules/Registration/ConfirmEmail';
import { RegistrationSuccess } from 'modules/Registration/RegistrationSuccess';
import { Registration } from 'modules/Registration/ui';

import { GuestOnlyGuard } from './guards/GuestOnlyGuard';
import { RoleGuard } from './guards/RoleGuard';
import { VerifiedAccountGuard } from './guards/VerifiedAccountGuard';
import { WalletPublicGuard } from './guards/WalletPublicGuard';
import classes from './index.module.scss';
import { RootRedirect } from './RootRedirect';
import { Details, DetailsNew } from '../modules/Details/Details';
import { DetailsMenu } from '../modules/Details/DetailsMenu/DetailsMenu';
import { DetailsProfile } from '../modules/Details/DetailsProfile/DetailsProfile';
import { DetailsRepProfile } from '../modules/Details/DetailsRep/DetailsRep';
import { StudentSearch } from '../modules/Details/DetailsSearch/DetailsSearch';
import { StudentTutorSortProvider } from '../modules/Details/StudentTutorSortContext';
import { ErrorPage, RouteErrorPage } from '../modules/E404/Error';
import { TeacherWallet } from '../modules/Teacher/components/TeacherWallet';
import { TeacherWalletCheckout } from '../modules/Teacher/components/TeacherWalletCheckout';

export const browserRouter = createBrowserRouter([
  {
    Component: PageWrapper,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: '/',
        element: <RootRedirect />,
      },
      {
        path: '/teacher',
        element: (
          <RoleGuard requiredRole={1}>
              <Teacher />
          </RoleGuard>
        ),
        children: [
          { index: true, element: <TeacherProfile /> },
          {
            path: 'chat',
            element: (
              <VerifiedAccountGuard editPath='/teacher/edit'>
                <ChatV2 />
              </VerifiedAccountGuard>
            ),
          },
          { path: 'edit', element: <TeacherEdit /> },
          { path: 'edits', element: <VerificationEdits editPath='/teacher/edit' /> },
          {
            path: 'search',
            element: (
              <VerifiedAccountGuard editPath='/teacher/edit'>
                <TeacherSearch />
              </VerifiedAccountGuard>
            ),
          },
          {
            path: 'search/error-one',
            element: (
              <VerifiedAccountGuard editPath='/teacher/edit'>
                <TeacherSearchError />
              </VerifiedAccountGuard>
            ),
          },
          {
            path: 'search/error-two',
            element: (
              <VerifiedAccountGuard editPath='/teacher/edit'>
                <TeacherSearchErrorWait />
              </VerifiedAccountGuard>
            ),
          },
          {
            path: 'search/error-three',
            element: (
              <VerifiedAccountGuard editPath='/teacher/edit'>
                <TeacherSearchErrorPay />
              </VerifiedAccountGuard>
            ),
          },
          { path: 'settings', element: <TeacherSettings /> },
          { path: 'notifications', element: <TeacherNotificationsPage /> },
          { path: 'trust-score', element: <TeacherTrustScorePage /> },
          { path: 'help', element: <TeacherHelp /> },
          {
            path: 'wallet',
            element: (
              <WalletPublicGuard>
                <VerifiedAccountGuard editPath='/teacher/edit'>
                  <TeacherWallet />
                </VerifiedAccountGuard>
              </WalletPublicGuard>
            ),
          },
          {
            path: 'wallet/checkout/:token',
            element: (
              <WalletPublicGuard>
                <VerifiedAccountGuard editPath='/teacher/edit'>
                  <TeacherWalletCheckout />
                </VerifiedAccountGuard>
              </WalletPublicGuard>
            ),
          },
          { path: 'questions', element: <Questions /> },
          {
            path: 'search/:id',
            element: (
              <VerifiedAccountGuard editPath='/teacher/edit'>
                <TeacherSearchFiltersItem />
              </VerifiedAccountGuard>
            ),
          },
          { path: '*', element: <ErrorPage code={404} embedded compact /> },
        ],
      },
      {
        path: '/password/confirm',
        element: <PasswordConfirm />,
      },
      {
        path: '/personal-data/confirm',
        element: <PersonalDataConfirmPage />,
      },
      {
        path: '/personal-data/reject',
        element: <PersonalDataRejectPage />,
      },
      {
        path: '/personal-data/otp',
        element: <PersonalDataOtpPage />,
      },
      {
        path: '/profile',
        element: (
          <RoleGuard requiredRole={2}>
            <Profile />
          </RoleGuard>
        ),

        children: [
          {
            index: true,
            element: (
              <>
                <ProfileMain />
                {/* <ProfileReview /> */}
              </>
            ),
          },
          {
            path: 'chats',
            element: (
              <VerifiedAccountGuard editPath='/profile'>
                <div className={classes.container}>
                  <ChatV2 />
                </div>
              </VerifiedAccountGuard>
            ),
          },
          {
            path: 'leave-review/:tutorUserId',
            element: <StudentReviewPage />,
          },
          {
            path: 'notifications',
            element: <NotificationsPage />,
          },

          {
            path: 'tasks',
            element: <Tasks />,
          },

          {
            // Новая задача: мастер и проверка условий внутри кабинета, рядом с меню
            // (Figma 55:35441), а не отдельной страницей без меню.
            path: 'details/task',
            element: (
              <VerifiedAccountGuard editPath='/profile'>
                <Details />
              </VerifiedAccountGuard>
            ),
          },
          {
            path: 'details/task/:id',
            element: (
              <VerifiedAccountGuard editPath='/profile'>
                <div className={classes.container}>
                  <DetailsMenu />
                  <DetailsNew />
                </div>
              </VerifiedAccountGuard>
            ),
          },
          {
            path: 'details/:id/rep/:repId',
            element: (
              <VerifiedAccountGuard editPath='/profile'>
                <div className={classes.container}>
                  <DetailsMenu />
                  <DetailsRepProfile StudentView={true} />
                </div>
              </VerifiedAccountGuard>
            ),
          },
          {
            path: 'details/:id/search',
            element: (
              <VerifiedAccountGuard editPath='/profile'>
                <StudentTutorSortProvider>
                  <div className={classes.container}>
                    <DetailsMenu />
                    <StudentSearch />
                  </div>
                </StudentTutorSortProvider>
              </VerifiedAccountGuard>
            ),
          },
          {
            path: 'details/:id/chats',
            element: (
              <VerifiedAccountGuard editPath='/profile'>
                <div className={classes.container}>
                  <DetailsMenu />
                  <ChatV2 useBulletinFromRoute />
                </div>
              </VerifiedAccountGuard>
            ),
          },
          {
            path: 'details/search/:id',
            element: (
              <VerifiedAccountGuard editPath='/profile'>
                <div className={classes.container}>
                  <DetailsMenu />
                  <DetailsProfile />
                </div>
              </VerifiedAccountGuard>
            ),
          },
          { path: 'settings', element: <Settings /> },
          { path: 'edits', element: <VerificationEdits editPath='/profile' /> },
          { path: 'questions', element: <Questions /> },
          { path: 'help', element: <Help /> },
          { path: '*', element: <ErrorPage code={404} embedded compact /> },
        ],
      },

      {
        path: '/ErrorPage',
        element: <ErrorPage code={404} embedded />,
        handle: { chrome: 'error' },
      },
      {
        path: '/login',
        handle: { chrome: 'error' },
        element: (
          <GuestOnlyGuard>
            <Login />
          </GuestOnlyGuard>
        ),
      },
      {
        path: '/login/confirm',
        handle: { chrome: 'error' },
        element: (
          <GuestOnlyGuard>
            <ConfirmPassword />
          </GuestOnlyGuard>
        ),
      },
      {
        path: '/registration',
        handle: { chrome: 'error' },
        element: <Registration />,
      },
      {
        path: '/register',
        element: <Navigate to='/registration' replace />,
      },
      {
        path: '/registration/confirm',
        handle: { chrome: 'error' },
        element: <ConfirmEmail />,
      },
      {
        path: '/registration/success',
        handle: { chrome: 'error' },
        element: <RegistrationSuccess />,
      },
      {
        path: '/otp',
        handle: { chrome: 'error' },
        element: <OtpChallengePage />,
      },
      {
        path: '/forgot-password',
        handle: { chrome: 'error' },
        element: <ForgotPassword />,
      },
      {
        path: '/reset-password',
        handle: { chrome: 'error' },
        element: <ResetPassword />,
      },
      {
        path: '/reset-success',
        handle: { chrome: 'error' },
        element: <ResetSuccess />,
      },
      {
        path: '/verification-form',
        // Легаси-заглушка (кнопки без обработчиков) - ведём на рабочую проверку паспорта.
        element: <Navigate to='/passport-confirm' replace />,
      },
      {
        path: '/passport-confirm',
        element: (
          <RoleGuard requiredRole={1}>
            <PassportCaptureWizard />
          </RoleGuard>
        ),
      },
      {
        path: '/passport-capture-test',
        element: (
          <RoleGuard requiredRole={1}>
            <PassportCaptureWizard />
          </RoleGuard>
        ),
      },
      {
        path: '/confirm-password',
        element: (
          <GuestOnlyGuard>
            <ConfirmPassword />
          </GuestOnlyGuard>
        ),
      },
      {
        path: '*',
        element: <ErrorPage code={404} embedded />,
        handle: { chrome: 'error' },
      },
    ],
  },
]);
